import { logError } from '../lib/logger';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  getCommandCenter, getAlerts, generateHuddleSummary, createActionAlert, dismissActionAlert,
  getTeamMembers, createPostHuddleAction, getAutoHuddleToday, resolveClaraFlag, getCarryForward
} from '../lib/api';
import {
  Warning, CheckCircle, Sparkle, Bell, Handshake, Buildings, Clock,
  X, ArrowRight, ClockCountdown, ChatCircleText, Fire, PlayCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import HuddleMode from './enterprise/HuddleMode';
import ConferenceRoomMode from './enterprise/ConferenceRoomMode';
import { Television } from '@phosphor-icons/react';

/* ─────────── enterprise design tokens ─────────── */
const SEV = {
  critical: { text: 'text-[color:var(--mr-red-strong)]', bg: 'bg-[color:var(--mr-red-wash)]', border: 'border-[color:#d9363e40]', dot: 'bg-[color:var(--mr-red)]' },
  high:     { text: 'text-[color:var(--mr-warn)]',       bg: 'bg-[color:var(--mr-warn-wash)]', border: 'border-[color:#a8611640]', dot: 'bg-[color:var(--mr-warn)]' },
  warning:  { text: 'text-[color:var(--mr-warn)]',       bg: 'bg-[color:var(--mr-warn-wash)]', border: 'border-[color:#a8611640]', dot: 'bg-[color:var(--mr-warn)]' },
  info:     { text: 'text-[color:var(--mr-blue-strong)]', bg: 'bg-[color:var(--mr-blue-wash)]', border: 'border-[color:#1e4ce040]', dot: 'bg-[color:var(--mr-blue)]' },
  good:     { text: 'text-[color:var(--mr-ok)]',         bg: 'bg-[color:var(--mr-ok-wash)]',   border: 'border-[color:#16716340]', dot: 'bg-[color:var(--mr-ok)]' },
  ai:       { text: 'text-[color:var(--mr-blue-strong)]', bg: 'bg-[color:var(--mr-blue-wash)]', border: 'border-[color:#1e4ce040]', dot: 'bg-[color:var(--mr-blue)]' },
  neutral:  { text: 'text-muted-foreground',             bg: 'bg-muted/40',                    border: 'border-border',             dot: 'bg-muted-foreground' },
};

/* ─────────── KPI Card ─────────── */
function KpiCard({ icon: Icon, tone = 'neutral', label, value, sub, subTone, onClick, testId }) {
  const s = SEV[tone] || SEV.neutral;
  const subT = SEV[subTone || 'neutral'] || SEV.neutral;
  return (
    <button onClick={onClick} className="text-left w-full" data-testid={testId}>
      <Card className="bg-card border-border hover:border-foreground/20 transition-colors h-full">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${s.bg} ${s.border}`}>
              <Icon size={18} weight="fill" className={s.text} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
              <p className="text-3xl font-bold text-foreground leading-tight mt-0.5">{value}</p>
              {sub && <p className={`text-xs mt-1 ${subT.text}`}>{sub}</p>}
            </div>
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

/* ─────────── Status chip ─────────── */
function StatusChip({ tone = 'neutral', children }) {
  const s = SEV[tone] || SEV.neutral;
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${s.bg} ${s.border} ${s.text}`}>{children}</span>;
}

/* ─────────── Format currency ─────────── */
function fmtVol(v) {
  if (!v) return '';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `$${Math.round(v / 1000)}K`;
  return `$${v}`;
}

/* ─────────── Command Queue Row ─────────── */
function QueueRow({ item, onOpenFile }) {
  const pri = SEV[item.priority] || SEV.neutral;
  return (
    <div className="grid grid-cols-12 items-center gap-3 px-4 py-2.5 border-b border-border/50 last:border-0 hover:bg-muted/20 transition-colors" data-testid={`queue-row-${item.source_type}-${item.source_id || ''}`}>
      <div className="col-span-3 flex items-center gap-2 min-w-0">
        <span className={`w-1.5 h-8 rounded-full ${pri.dot} flex-shrink-0`} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{item.borrower}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{item.category.replace('_', ' ')}</p>
        </div>
      </div>
      <div className="col-span-5 min-w-0">
        <p className="text-sm text-foreground line-clamp-1">{item.issue}</p>
      </div>
      <div className="col-span-1 text-center">
        {item.owner ? <span className="inline-flex w-7 h-7 rounded-md bg-muted text-[11px] font-bold text-foreground items-center justify-center">{item.owner}</span> : <span className="text-xs text-muted-foreground">—</span>}
      </div>
      <div className="col-span-2 text-xs font-semibold whitespace-nowrap">
        <span className={pri.text}>{item.due_label}</span>
      </div>
      <div className="col-span-1 text-right">
        <button onClick={() => onOpenFile(item)} className="inline-flex items-center gap-1 text-xs font-medium text-foreground/70 hover:text-foreground" data-testid={`queue-open-${item.source_id || ''}`}>
          Open <ArrowRight size={11} />
        </button>
      </div>
    </div>
  );
}

/* ─────────── Alerts row ─────────── */
function AlertRow({ a, onDismiss, onResolveFlag }) {
  const tone = a.severity === 'critical' ? 'critical' : a.severity === 'warning' ? 'high' : 'info';
  const s = SEV[tone];
  const isFlag = a.type === 'clara_flag';
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 last:border-0 hover:bg-muted/20">
      <span className={`w-2 h-2 rounded-full ${s.dot} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate">{a.message}</p>
        {a.borrower && <p className="text-[10px] text-muted-foreground">{a.borrower}</p>}
      </div>
      <StatusChip tone={tone}>{a.severity.toUpperCase()}</StatusChip>
      {isFlag && onResolveFlag && (
        <button onClick={() => onResolveFlag(a.id)} className="text-[color:var(--mr-ok)] hover:opacity-80" title="Resolve" data-testid={`resolve-flag-${a.id}`}><CheckCircle size={16} /></button>
      )}
      {!isFlag && a.id && onDismiss && (
        <button onClick={() => onDismiss(a.id)} className="text-muted-foreground hover:text-foreground" title="Dismiss" data-testid={`dismiss-alert-${a.id}`}><X size={14} /></button>
      )}
    </div>
  );
}

/* ─────────── AI Huddle Agenda item ─────────── */
function AgendaItem({ item, idx, isDone, isAssigning, members, assignTo, setAssignTo, onAssign, onComplete, onAddToAlerts, onDismiss, setAssigningItem }) {
  const tone = item.priority === 'critical' ? 'critical' : item.priority === 'high' ? 'high' : 'neutral';
  const s = SEV[tone];
  return (
    <div className={`rounded-md border ${s.border} ${s.bg} p-3`} data-testid={`agenda-item-${idx}`}>
      <div className="flex items-start gap-3">
        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${s.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <p className="text-sm font-bold text-foreground">{item.borrower}</p>
            {item.owner && <StatusChip tone="neutral">{item.owner}</StatusChip>}
            {item.deadline && <span className="text-[10px] text-muted-foreground">· {item.deadline}</span>}
            {isDone && <StatusChip tone="good">DONE</StatusChip>}
          </div>
          <p className="text-sm text-foreground/80 mt-1">{item.action}</p>
        </div>
      </div>
      {!isDone && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {isAssigning ? (
            <>
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger className="h-7 w-32 text-xs border-border"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>{members.map(m => <SelectItem key={m.initials} value={m.initials}>{m.initials} · {m.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" onClick={() => onAssign(item, idx)} className="h-7 text-[11px] mr-btn-primary" data-testid={`confirm-assign-${idx}`}>Confirm</Button>
              <Button size="sm" variant="outline" onClick={() => { setAssigningItem(null); setAssignTo(''); }} className="h-7 text-[11px] border-border">Cancel</Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={() => onComplete(item, idx)} className="h-7 text-[11px] bg-[color:var(--mr-ok)] hover:bg-[color:#0d5a4f] text-white" data-testid={`agenda-complete-${idx}`}>
                <CheckCircle size={11} className="mr-1" /> Complete
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAssigningItem(idx)} className="h-7 text-[11px] border-border" data-testid={`agenda-assign-${idx}`}>
                Assign
              </Button>
              <Button size="sm" variant="outline" onClick={() => onAddToAlerts(item)} className="h-7 text-[11px] border-border" data-testid={`agenda-alert-${idx}`}>
                To Alerts
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDismiss(idx)} className="h-7 text-[11px] text-muted-foreground" data-testid={`agenda-dismiss-${idx}`}>
                <X size={12} />
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────── Main Dashboard ─────────── */
export default function Dashboard({ onNavigate, onFilterNavigate, currentUser }) {
  const [cc, setCc] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [aiResult, setAiResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [members, setMembers] = useState([]);
  const [dismissedItems, setDismissedItems] = useState({});
  const [completedItems, setCompletedItems] = useState({});
  const [assigningItem, setAssigningItem] = useState(null);
  const [assignTo, setAssignTo] = useState('');
  const [showAllQueue, setShowAllQueue] = useState(false);
  const [huddleOpen, setHuddleOpen] = useState(false);
  const [conferenceOpen, setConferenceOpen] = useState(false);
  const [carry, setCarry] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [c, a, m, autoH, cf] = await Promise.all([getCommandCenter(), getAlerts(), getTeamMembers(), getAutoHuddleToday(), getCarryForward().catch(() => null)]);
      setCc(c); setAlerts(a); setMembers(m); setCarry(cf);
      if (autoH?.items?.length > 0 && !aiResult) setAiResult(autoH);
    } catch (e) { logError('Dashboard load error:', e); toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [aiResult]);
  useEffect(() => { loadData(); const iv = setInterval(loadData, 15000); return () => clearInterval(iv); }, [loadData]);

  const handleGenerate = async () => {
    setLoadingSummary(true); setDismissedItems({}); setCompletedItems({});
    try { const r = await generateHuddleSummary(); setAiResult(r); toast.success('Agenda ready'); }
    catch (e) { toast.error('Failed to generate'); } finally { setLoadingSummary(false); }
  };
  const handleAddToAlerts = async (item) => {
    try {
      await createActionAlert({ message: `${item.borrower}: ${item.action}`, source: 'ai',
        severity: item.priority === 'critical' ? 'critical' : item.priority === 'high' ? 'warning' : 'info', borrower: item.borrower });
      toast.success('Saved as active alert'); loadData();
    } catch (e) { toast.error('Failed'); }
  };
  const handleDismissItem = (idx) => { setDismissedItems(p => ({ ...p, [idx]: true })); toast.success('Dismissed'); };
  const handleCompleteItem = async (item, idx) => {
    try {
      await createPostHuddleAction({ borrower: item.borrower || 'General', action: item.action, assigned_to: item.owner || '', deadline: item.deadline || 'Completed' });
      setCompletedItems(p => ({ ...p, [idx]: true })); toast.success('Marked complete');
    } catch (e) { toast.error('Failed'); }
  };
  const handleAssignItem = async (item, idx) => {
    if (!assignTo) { toast.error('Select a team member'); return; }
    try {
      await createPostHuddleAction({ borrower: item.borrower || 'General', action: item.action, assigned_to: assignTo, deadline: item.deadline || 'End of day' });
      toast.success(`Assigned to ${assignTo}`); setAssigningItem(null); setAssignTo('');
    } catch (e) { toast.error('Failed'); }
  };
  const handleDismissAlert = async (id) => { try { await dismissActionAlert(id); loadData(); } catch (e) { toast.error('Failed'); } };
  const handleResolveFlag = async (id) => { try { await resolveClaraFlag(id); toast.success('Flag resolved'); loadData(); } catch (e) { toast.error('Failed'); } };

  const openQueueItem = (item) => {
    if (item.source_type === 'hot_file') onNavigate('files');
    else if (item.source_type === 'commitment') onNavigate('commitment-dates');
    else if (item.source_type === 'escalation') onNavigate('escalations');
    else if (item.source_type === 'clara_flag') onNavigate('clara');
  };

  if (loading || !cc) return <div className="flex items-center justify-center h-64" data-testid="dashboard-loading"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>;

  const { kpis, queue } = cc;
  const visibleAgenda = (aiResult?.items || []).filter((_, i) => !dismissedItems[i]);
  const visibleQueue = showAllQueue ? queue : queue.slice(0, 6);
  const criticalAlerts = alerts.filter(a => a.severity === 'critical').slice(0, 6);
  const otherAlerts = alerts.filter(a => a.severity !== 'critical').slice(0, 6);

  const huddleTime = '9:00 AM';
  const now = new Date();
  const [hh, mm] = huddleTime.replace(' AM', '').split(':');
  const target = new Date(); target.setHours(+hh, +mm, 0, 0);
  const diff = target - now;
  const liveLabel = diff > 0 && diff < 6 * 3600 * 1000
    ? `Live in ${Math.round(diff / 60000)}m`
    : (diff <= 0 && diff > -60 * 60000 ? 'In progress' : '');

  return (
    <div className="space-y-5" data-testid="dashboard-view">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground">Daily Huddle Command Center</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Everything that needs a decision, action, or follow up today.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="hidden sm:flex flex-col items-end">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Daily Huddle</p>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-foreground">{huddleTime}</span>
              {liveLabel && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[color:var(--mr-ok)]"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--mr-ok)] animate-pulse" />{liveLabel}</span>}
            </div>
          </div>
          <Button variant="outline" onClick={() => setConferenceOpen(true)} className="h-9 border-border text-sm" data-testid="conference-room-btn" title="Wall-TV huddle display">
            <Television size={15} className="mr-1.5" /> Conference Room
          </Button>
          <Button onClick={() => setHuddleOpen(true)} className="h-9 mr-btn-primary text-sm px-4" data-testid="start-huddle-btn">
            <PlayCircle size={15} weight="fill" className="mr-1.5" /> Start Huddle
          </Button>
          <Button variant="outline" onClick={handleGenerate} disabled={loadingSummary} className="h-9 border-border text-sm" data-testid="view-agenda-btn">
            {loadingSummary ? 'Preparing...' : (aiResult?.items?.length ? 'Refresh Agenda' : 'Generate Agenda')}
          </Button>
        </div>
      </div>

      {/* Carry Forward Strip */}
      {carry?.count > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid="carry-forward-strip">
          <div className="flex items-center gap-2">
            <Clock size={14} weight="fill" className="text-amber-500" />
            <span className="text-xs font-bold text-amber-500 uppercase tracking-wider">Carried Forward</span>
          </div>
          <span className="text-sm text-foreground">{carry.count} item{carry.count !== 1 ? 's' : ''} from yesterday still open</span>
          <span className="text-[11px] text-muted-foreground truncate flex-1">
            {carry.items.slice(0, 3).map(i => i.borrower).join(', ')}{carry.items.length > 3 ? `, +${carry.items.length - 3} more` : ''}
          </span>
        </div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-row">
        <KpiCard icon={Warning} tone="critical" label="Needs Action Today"
          value={kpis.needs_action_today}
          sub={kpis.needs_action_critical > 0 ? `${kpis.needs_action_critical} critical` : 'On track'}
          subTone={kpis.needs_action_critical > 0 ? 'critical' : 'good'}
          onClick={() => onFilterNavigate('files', 'urgent')}
          testId="kpi-needs-action" />
        <KpiCard icon={ClockCountdown} tone="high" label="Commitments at Risk"
          value={kpis.commitments_at_risk}
          sub={kpis.commitments_overdue > 0 ? `${kpis.commitments_overdue} overdue` : 'Within 7 days'}
          subTone={kpis.commitments_overdue > 0 ? 'critical' : 'neutral'}
          onClick={() => onNavigate('commitment-dates')}
          testId="kpi-commitments" />
        <KpiCard icon={ChatCircleText} tone="high" label="Client Experience"
          value={kpis.client_experience_alerts}
          sub={kpis.client_experience_new_today > 0 ? `${kpis.client_experience_new_today} new today` : 'Clara flags active'}
          subTone="high"
          onClick={() => onNavigate('clara')}
          testId="kpi-cx" />
        <KpiCard icon={Handshake} tone="info" label="Waiting on 3rd Party"
          value={kpis.waiting_third_party}
          sub="Borrower / Realtor / Title"
          onClick={() => onFilterNavigate('files', 'follow_up')}
          testId="kpi-third-party" />
        <KpiCard icon={CheckCircle} tone="good" label="Clear for Today"
          value={kpis.clear_for_today}
          sub="On track"
          subTone="good"
          onClick={() => onNavigate('files')}
          testId="kpi-clear" />
        <KpiCard icon={Buildings} tone="info" label="Closings This Week"
          value={kpis.closings_this_week}
          sub={kpis.closings_this_week_volume ? fmtVol(kpis.closings_this_week_volume) : 'Next 7 days'}
          onClick={() => onNavigate('files')}
          testId="kpi-closings" />
      </div>

      {/* Command Queue + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="bg-card border-border lg:col-span-3" data-testid="command-queue">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Fire size={16} weight="fill" className="text-[color:var(--mr-red)]" />
                <h2 className="text-base font-bold text-foreground">Today&apos;s Command Queue</h2>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-semibold">{queue.length}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">Prioritized by impact and urgency.</p>
            </div>
            <button onClick={() => setShowAllQueue(!showAllQueue)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="toggle-queue">
              {showAllQueue ? 'Show less' : 'View all'}
            </button>
          </div>
          <div className="grid grid-cols-12 gap-3 px-4 py-2 border-b border-border/50 text-[10px] font-semibold uppercase text-muted-foreground tracking-wide">
            <div className="col-span-3">Borrower</div>
            <div className="col-span-5">Issue</div>
            <div className="col-span-1 text-center">Owner</div>
            <div className="col-span-2">Due / Impact</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {visibleQueue.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <CheckCircle size={28} weight="duotone" className="text-[color:var(--mr-ok)] mx-auto mb-2" />
              Nothing needs immediate attention. Nice work.
            </div>
          ) : (
            visibleQueue.map(item => (
              <QueueRow key={`${item.source_type}-${item.source_id || item.borrower}`} item={item} onOpenFile={openQueueItem} />
            ))
          )}
        </Card>

        <Card className="bg-card border-border lg:col-span-2" data-testid="active-alerts">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={16} weight="fill" className="text-[color:var(--mr-red)]" />
              <h2 className="text-base font-bold text-foreground">Active Alerts</h2>
              <span className="text-[10px] px-1.5 py-0.5 rounded chip-critical border font-semibold">{alerts.length}</span>
            </div>
          </div>
          {alerts.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <CheckCircle size={28} weight="duotone" className="text-[color:var(--mr-ok)] mx-auto mb-2" /> No active alerts
            </div>
          ) : (
            <>
              {criticalAlerts.map(a => <AlertRow key={a.id || a.message} a={a} onDismiss={handleDismissAlert} onResolveFlag={handleResolveFlag} />)}
              {otherAlerts.map(a => <AlertRow key={a.id || a.message} a={a} onDismiss={handleDismissAlert} onResolveFlag={handleResolveFlag} />)}
            </>
          )}
        </Card>
      </div>

      {/* AI Agenda */}
      <Card className="bg-card border-border" data-testid="ai-summary-card">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[color:var(--mr-blue-wash)] border border-[color:#1e4ce040] flex items-center justify-center">
              <Sparkle size={14} weight="fill" className="text-[color:var(--mr-blue-strong)]" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">AI Huddle Agenda</h2>
              <p className="text-[11px] text-muted-foreground">Clara prepared this from your live operational data.</p>
            </div>
            {aiResult?.items?.length > 0 && <StatusChip tone="ai">AI PREPARED</StatusChip>}
          </div>
          <Button onClick={handleGenerate} disabled={loadingSummary} size="sm" className="h-8 mr-btn-primary" data-testid="generate-summary-btn">
            {loadingSummary ? <><div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent mr-1.5" />Preparing...</>
              : <><Sparkle size={12} weight="fill" className="mr-1" />{aiResult?.items?.length ? 'Refresh Agenda' : 'Generate'}</>}
          </Button>
        </div>
        <div className="p-4">
          {visibleAgenda.length > 0 ? (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1" data-testid="ai-summary-items">
              {aiResult.items.map((item, i) => {
                if (dismissedItems[i]) return null;
                return (
                  <AgendaItem key={`ai-${item.borrower}-${i}`} item={item} idx={i}
                    isDone={completedItems[i]} isAssigning={assigningItem === i}
                    members={members} assignTo={assignTo} setAssignTo={setAssignTo}
                    onAssign={handleAssignItem} onComplete={handleCompleteItem}
                    onAddToAlerts={handleAddToAlerts} onDismiss={handleDismissItem}
                    setAssigningItem={setAssigningItem} />
                );
              })}
              {aiResult?.generated_at && <p className="text-[10px] text-muted-foreground pt-1">Generated {new Date(aiResult.generated_at).toLocaleString()}</p>}
            </div>
          ) : aiResult?.summary ? (
            <div className="bg-background border border-border rounded-md p-4 text-sm whitespace-pre-wrap text-foreground/80" data-testid="ai-summary-text">{aiResult.summary}</div>
          ) : (
            <div className="border border-dashed border-border rounded-md p-6 text-center bg-background/50">
              <Sparkle size={24} weight="duotone" className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Generate a prioritized agenda for your morning huddle.</p>
            </div>
          )}
        </div>
      </Card>

      <HuddleMode open={huddleOpen} onClose={() => setHuddleOpen(false)} currentUser={currentUser} />
      <ConferenceRoomMode open={conferenceOpen} onClose={() => setConferenceOpen(false)} />
    </div>
  );
}
